/**
 * PasswordInputModal
 * Modal component for entering password for PFX certificate generation
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';

import { validateCertificatePassword } from '../../../common/spdCore/contexts/CertificateContext';
import ValidationAlert from '../../../app/components/ValidationAlert';

interface PasswordInputModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: (password: string) => void;
  isLoading?: boolean;
}

export const PasswordInputModal: React.FC<PasswordInputModalProps> = ({
  show,
  onHide,
  onConfirm,
  isLoading = false,
}) => {
  const [password, setPassword] = useState<string>('');
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    if (show) {
      setPassword('');
      setShowValidationErrors(false);
      setValidationErrors([]);
    }
  }, [show]);

  const handleConfirm = async () => {
    setShowValidationErrors(false);

    const status = await validateCertificatePassword({ password });
    if (!status.valid) {
      setValidationErrors(status.errors);
      setShowValidationErrors(true);
      return;
    }

    onConfirm(password);
  };

  const handleClose = () => {
    setPassword('');
    setShowValidationErrors(false);
    setValidationErrors([]);
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered={true}
      backdrop="static"
      backdropClassName="modal-on-modal-backdrop"
      className="modal-on-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Hasło do certyfikatu</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Hasło</Form.Label>
            <Form.Control
              type="text"
              placeholder="Wprowadź hasło"
              value={password}
              onChange={e => {
                setPassword(e.target.value);
              }}
              disabled={isLoading}
            />
          </Form.Group>
        </Form>

        {showValidationErrors && (
          <div className="mt-4">
            <ValidationAlert
              show={showValidationErrors}
              errors={validationErrors}
              onHide={() => setShowValidationErrors(false)}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
          Anuluj
        </Button>
        <Button variant="primary" onClick={handleConfirm} disabled={isLoading}>
          {isLoading ? 'Pobieranie...' : 'Pobierz'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
